export type ChartKind =
  | 'metric'
  | 'line'
  | 'area'
  | 'bar'
  | 'bar-horizontal'
  | 'pie'
  | 'table';
export type AggregateOp = 'sum' | 'avg' | 'rate';
export type SegmentType = 'day' | 'device' | 'source' | 'page' | string;

export interface ChartLayout {
  /** Order in the grid */
  order?: number;
  /** Number of columns to span on mobile (default: 1) */
  mobile?: { cols?: number; rows?: number };
  /** Number of columns to span on tablet (default: 1) */
  tablet?: { cols?: number; rows?: number };
  /** Number of columns to span on desktop (default: 1) */
  desktop?: { cols?: number; rows?: number };
}

export interface ChartDefinition {
  id: string;
  title: string;
  kind: ChartKind;
  metrics: string[];
  segment?: SegmentType;
  aggregate?: AggregateOp | AggregateOp[];
  comparison?: boolean;
  layout?: ChartLayout;
  /** For pie charts: minimum legend items to always show (will add missing items with value 0) */
  minLegendItems?: string[];
}

export type AnalyticsConfig = ChartDefinition[];

export const defaultOverviewConfig: AnalyticsConfig = [
  {
    id: 'kpi_visitors',
    title: 'Total Visitors',
    kind: 'metric',
    metrics: ['visitors'],
    aggregate: 'sum',
    comparison: true,
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 4 },
      desktop: { cols: 4 },
    },
  },
  {
    id: 'kpi_conversions',
    title: 'Total Conversions',
    kind: 'metric',
    metrics: ['conversions'],
    aggregate: 'sum',
    comparison: true,
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 4 },
      desktop: { cols: 4 },
    },
  },
  {
    id: 'kpi_cvr',
    title: 'Avg Conversion Rate',
    kind: 'metric',
    metrics: ['cvr'],
    aggregate: 'avg',
    comparison: true,
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 4 },
      desktop: { cols: 4 },
    },
  },
  {
    id: 'top_pages',
    title: 'Top Funnels',
    kind: 'bar-horizontal',
    metrics: ['visitors'],
    segment: 'campaignId',
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 12 },
      desktop: { cols: 4 },
    },
  },
  {
    id: 'top_referrers',
    title: 'Top Referrers',
    kind: 'bar-horizontal',
    metrics: ['visitors'],
    segment: 'referrerDomain',
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 12 },
      desktop: { cols: 4 },
    },
  },
  {
    id: 'top_ad_networks',
    title: 'Top Ad Networks',
    kind: 'bar-horizontal',
    metrics: ['visitors'],
    segment: 'adNetworkProvider',
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 12 },
      desktop: { cols: 4 },
    },
  },
  {
    id: 'trend_main',
    title: 'Trends Over Time',
    kind: 'area',
    metrics: ['visitors', 'conversions', 'cvr', 'leads'],
    segment: 'day',
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 12 },
      desktop: { cols: 12 },
    },
  },
  {
    id: 'breakdown_device',
    title: 'Device Breakdown',
    kind: 'pie',
    metrics: ['visitors'],
    segment: 'device',
    minLegendItems: ['mobile', 'desktop', 'tablet'],
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 6 },
      desktop: { cols: 4 },
    },
  },
  {
    id: 'breakdown_browser',
    title: 'Browser Breakdown',
    kind: 'pie',
    metrics: ['visitors'],
    segment: 'browser',
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 6 },
      desktop: { cols: 4 },
    },
  },
  {
    id: 'breakdown_os',
    title: 'OS Breakdown',
    kind: 'pie',
    metrics: ['visitors'],
    segment: 'os',
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 6 },
      desktop: { cols: 4 },
    },
  },
  {
    id: 'breakdown_country',
    title: 'Country Breakdown',
    kind: 'pie',
    metrics: ['visitors'],
    segment: 'country',
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 6 },
      desktop: { cols: 4 },
    },
  },
  {
    id: 'breakdown_city',
    title: 'City Breakdown',
    kind: 'pie',
    metrics: ['visitors'],
    segment: 'city',
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 6 },
      desktop: { cols: 4 },
    },
  },
  {
    id: 'breakdown_bots',
    title: 'Bot Traffic Breakdown',
    kind: 'pie',
    metrics: ['visitors'],
    segment: 'botName',
    layout: {
      mobile: { cols: 12 },
      tablet: { cols: 6 },
      desktop: { cols: 4 },
    },
  },
];
